import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isDirty, reduxForm } from 'redux-form';
import { pending } from 'redux-saga-thunk';
import { notification } from 'antd';
import { withRouter } from 'react-router';
import get from 'lodash/get';
import clone from 'lodash/clone';
import endsWith from 'lodash/endsWith';
import { fromEntities, fromResource } from '../../store/selectors';
import Forecast from '../../components/pages/Forecast/index';
import {
  resourceCreateRequest,
  resourceDetailReadRequest,
  resourceListReadRequest,
  resourceUpdateRequest,
} from '../../store/resource/actions';
import { createValidator, required } from '../../services/validation/index';

class ForecastContainer extends Component {
  state = {
    calculating: false,
    saving: false,
    activeMenus: [],
  };

  componentDidMount() {
    this.refresh(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (get(nextProps, 'params.revisionId') !== get(this.props, 'params.revisionId')) {
      this.refresh(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loading && !this.props.loading) {
      const menus = this.props.forecast.status === 0 ? ['targeting'] : ['results', 'targeting'];
      this.setState({ activeMenus: menus });
    }
  }

  changeActiveKey = key => {
    this.setState({ activeMenus: key });
  };

  refresh = ({ getForecast, getForecastRevision, getRevisionList }) => {
    Promise.all([getForecast(), getForecastRevision(), getRevisionList()]).catch(e =>
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not retrieve forecast.',
        duration: 0,
      })
    );
  };

  handleRevisionSelect = id => {
    this.props.history.push(`/forecasts/${this.props.match.params.forecastId}/revision/${id}`);
  };

  getAudiencePromises = () => {
    const { resultData, updateAudience, audienceList } = this.props;
    const audienceItems = get(resultData, 'audience', []);

    const promises = [];
    audienceList.forEach(item => {
      const data = {
        id: item.id,
        included: Number(get(audienceItems, [item.id, 'included'], 0)),
        adjust_factor: Number(get(audienceItems, [item.id, 'adjFactor'], 100)) / 100,
        cpm: Number(get(audienceItems, [item.id, 'cost'], 7)),
      };

      promises.push(updateAudience(data));
    });

    return promises;
  };

  handleSave = () => {
    this.setState({ saving: true });
    const {
      forecastAdjustments,
      currentData,
      updateForecast,
      resultData: { audience, ...resultData },
      updateForecastRevision,
    } = this.props;

    const { start_date, end_date, notes, name } = currentData;
    return Promise.all(this.getAudiencePromises())
      .then(() => updateForecast({ name }))
      .then(() =>
        updateForecastRevision({
          ...resultData,
          start_date,
          end_date,
          notes,
          name,
          ...forecastAdjustments,
        })
      )
      .then(() => {
        this.setState({ saving: false });
        notification.success({
          message: 'Success',
          description: 'Forecast revision saved',
          duration: 4,
        });
      })
      .catch(e => {
        console.log(e);
        this.setState({ saving: false });
        return notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not save forecast revision.',
          duration: 0,
        });
      });
  };

  handleSaveRun = () => {
    this.setState({ calculating: true });
    const {
      currentData,
      resultData: { audience, ...resultData },
      updateForecastRevision,
      forecastAdjustments,
      updateForecast,
      forecast,
    } = this.props;

    // Only shows when revision is in draft state
    if (forecast.status !== 0) {
      this.setState({ calculating: false });
      return false;
    }

    const { start_date, end_date, notes, name } = currentData;
    return Promise.all(this.getAudiencePromises())
      .then(() => updateForecast({ name }))
      .then(() =>
        updateForecastRevision({
          ...resultData,
          start_date,
          end_date,
          notes,
          name,
          ...forecastAdjustments,
          status_id: 1,
          publish: true,
        })
      )
      .then(() => {
        const currents = this.state.activeMenus;
        this.setState({ calculating: false, activeMenus: [...currents, 'results'] });

        notification.success({
          message: 'Success',
          description: 'Forecast revision saved and calculations ran',
          duration: 4,
        });
      })
      .catch(e => {
        this.setState({ calculating: false });

        return notification.error({
          message: 'Error',
          description:
            typeof e === 'string' ? e : 'Could not save forecast revision nor run calculations.',
          duration: 0,
        });
      });
  };

  handleCreateRevision = () => {
    const {
      currentData,
      resultData: { audience, ...resultData },
      createForecastRevision,
      updateForecast,
      forecastAdjustments,
      forecast,
    } = this.props;
    const { start_date, end_date, notes, name } = currentData;
    return Promise.all(this.getAudiencePromises())
      .then(() => updateForecast({ name }))
      .then(() =>
        createForecastRevision({
          ...resultData,
          start_date,
          end_date,
          notes,
          name,
          ...forecastAdjustments,
          current_revision_id: forecast.revision_id,
        })
      )
      .then(id => {
        this.props.history.push(`/forecasts/${forecast.id}/revision/${id}`);
        notification.success({
          message: 'Success',
          description: 'Forecast revision created.',
          duration: 4,
        });
      })
      .catch(e =>
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not create forecast revision.',
          duration: 0,
        })
      );
  };

  render() {
    const { calculating, saving, activeMenus } = this.state;

    return (
      <Forecast
        calculating={calculating}
        saving={saving}
        changeActiveKey={this.changeActiveKey}
        handleCreateRevision={this.handleCreateRevision}
        handleSave={this.handleSave}
        handleSaveRun={this.handleSaveRun}
        handleRevisionSelect={this.handleRevisionSelect}
        {...this.props}
        activeMenus={activeMenus}
      />
    );
  }
}

const keysToNumbers = r => {
  if (!r) return {};

  const o = clone(r);

  // On save, send it in as the factor of percent
  Object.keys(o).forEach(k => {
    if (endsWith(k, 'adj_factor') || k === 'ease_of_scan' || k === 'inpath_pct_of_requests') {
      o[k] /= 100;
    }

    // But not ease_of_scan or inpath_pct_of_requests because that's handled above for a percentage
    const adjustmentData = ['inhand_product_count', 'avg_ad_request_per_device'];

    // Convert to number to pass api validation
    if (
      adjustmentData.includes(k) ||
      endsWith(k, '_cpe') ||
      endsWith(k, 'cpm') ||
      endsWith(k, 'included')
    ) {
      o[k] = Number(o[k]);
    }
  });

  return o;
};

const mapStateToProps = (
  { modal, ...state },
  {
    match: {
      params: { forecastId, revisionId },
    },
  }
) => {
  const forecastDetail = fromEntities.getDetail(state, 'forecast', forecastId) || {};
  const revisionDetail = fromEntities.getDetail(state, 'forecastRevision', revisionId) || {};
  const forecast = { ...forecastDetail, ...revisionDetail };
  const revisionList =
    fromEntities.getList(
      state,
      'forecastRevision',
      fromResource.getList(state, 'forecastRevision')
    ) || [];

  const location = get(state, `resource.forecastRevisionChain.count`, 0);
  const chain = get(state, `resource.forecastRevisionLocation.count`, 0);
  const resultData = get(state, 'form.ForecastResults.values', {});
  const currentData = get(state, 'form.Forecast.values', {});
  const forecastAdjustments = get(state, 'form.ForecastAdjustments.values', {});

  const warnDirty =
    isDirty('ForecastResults')(state) ||
    isDirty('Forecast')(state) ||
    isDirty('ForecastAdjustments')(state);

  const audienceListEntity = fromEntities.getList(
    state,
    'forecastRevisionAudience',
    fromResource.getList(state, 'forecastRevisionAudience')
  );

  const audienceList = audienceListEntity.map(a => ({
    id: a.id,
    included: a.included,
    title: `Audience: ${a.name}`,
    adjFactor: a.adjust_factor,
    dailyIE: a.daily_impressions,
    totalIE: 0,
    cost: a.cpm,
    totalNet: 0,
  }));

  return {
    audienceList,
    warnDirty,
    resultData: keysToNumbers(resultData),
    currentData: keysToNumbers(currentData),
    forecastAdjustments: keysToNumbers(forecastAdjustments),
    hasLocationOrChain: !!(location || chain),
    revisionList: revisionList.map(r => ({ label: r.start_date, value: r.revision_id })),
    forecast,
    loading: pending(state, 'forecastDetailRead'),
    creating: pending(state, 'forecastRevisionCreate'),
    initialValues: {
      ...forecast,
      revision: `${revisionId}`,
    },
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { forecastId, revisionId },
    },
  }
) => ({
  updateAudience: data =>
    dispatch(
      resourceUpdateRequest(
        'forecast/:forecastId/revision/:revisionId/audience/:id',
        { forecastId, id: data.id, revisionId },
        data
      )
    ),
  getForecast: () => dispatch(resourceDetailReadRequest('forecast/:id', { id: forecastId })),
  getForecastRevision: () =>
    dispatch(
      resourceDetailReadRequest('forecast/:forecastId/revision/:id', { id: revisionId, forecastId })
    ),
  getRevisionList: () =>
    dispatch(
      resourceListReadRequest('forecast/:forecastId/revision', { page_size: 100 }, { forecastId })
    ),
  createForecastRevision: data =>
    dispatch(resourceCreateRequest('forecast/:forecastId/revision', { forecastId }, data)),
  updateForecastRevision: data =>
    dispatch(
      resourceUpdateRequest(
        'forecast/:forecastId/revision/:id',
        { id: revisionId, forecastId },
        data
      )
    ),
  updateForecast: data => dispatch(resourceUpdateRequest('forecast/:id', { id: forecastId }, data)),
});

const validate = createValidator({
  name: [required],
  start_date: [required],
  end_date: [required],
});

export { keysToNumbers };

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  reduxForm({
    validate,
    form: 'Forecast',
    enableReinitialize: true,
  })
)(ForecastContainer);
